import { lazy, Suspense } from "react";
import { FACE_BY_ID } from "~/lib/faces";
import { useMarkFaceVisited } from "~/lib/hooks/useMarkFaceVisited";
import { useFaceSeo } from "~/lib/seo/useSeo";

const ReactNativeStage = lazy(() =>
  import("~/three/ReactNativeStage").then((m) => ({
    default: m.ReactNativeStage,
  })),
);

interface Reason {
  title: string;
  body: string;
}

const REASONS: readonly Reason[] = [
  {
    title: "One codebase",
    body: "TypeScript end-to-end. Shared business logic, shared validation, shared API clients. iOS and Android stay in lockstep — never one ahead of the other.",
  },
  {
    title: "Native escape hatches",
    body: "When RN can't hit a performance bar, drop into native modules (Kotlin / Swift) for that surface only. The team doesn't pivot, the codebase does.",
  },
  {
    title: "Expo + EAS",
    body: "Managed and bare workflows both. EAS Build for reproducible CI, OTA updates for rapid hotfixes, ad-hoc + TestFlight + internal-track distribution.",
  },
  {
    title: "Production-grade",
    body: "Hermes engine, sentry-style observability, deep links, push (FCM + APNs), in-app purchases, biometrics, secure storage, accessibility audits.",
  },
];

export function ReactNative() {
  const face = FACE_BY_ID["react-native"];
  useMarkFaceVisited(face.id);
  useFaceSeo(face);

  return (
    <section className="flex w-full flex-col gap-10 pb-16">
      <div className="relative h-56 w-full overflow-hidden border-b border-jg-border/40 sm:h-72">
        <Suspense
          fallback={
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-b from-jg-react-native/15 to-transparent"
            />
          }
        >
          <ReactNativeStage />
        </Suspense>

        <div className="pointer-events-none absolute right-4 top-4 sm:right-6">
          <span
            className="font-mono text-xs uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            Cubie · {face.label}
          </span>
        </div>
      </div>

      <header className="mx-auto w-full max-w-4xl px-4">
        <h1 className="font-display text-3xl font-semibold leading-tight tracking-tight text-jg-fg sm:text-4xl">
          One <span style={{ color: face.themeColor }}>codebase</span>, two
          stores
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-jg-fg-muted sm:text-base">
          Cross-platform mobile with React Native — TypeScript end to end,
          native escape hatches where you need them, Expo + EAS for the
          build pipeline.
        </p>
      </header>

      <div className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-4">
        <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
          <p
            className="font-mono text-[10px] uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            Why React Native
          </p>
          <ul className="mt-3 grid gap-4 sm:grid-cols-2">
            {REASONS.map((r) => (
              <li key={r.title} className="flex flex-col gap-1">
                <h4 className="font-display text-sm font-semibold text-jg-fg">
                  {r.title}
                </h4>
                <p className="text-sm leading-relaxed text-jg-fg-muted">
                  {r.body}
                </p>
              </li>
            ))}
          </ul>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: face.themeColor }}
            >
              Stack
            </p>
            <ul className="mt-3 flex flex-wrap gap-1.5">
              {[
                "TypeScript",
                "React Native",
                "Expo",
                "EAS Build",
                "Hermes",
                "React Navigation",
                "Reanimated",
                "MMKV",
                "FCM",
                "APNs",
              ].map((t) => (
                <li
                  key={t}
                  className="rounded-full border border-jg-border bg-jg-bg/40 px-2.5 py-0.5 text-xs text-jg-fg-muted"
                >
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: face.themeColor }}
            >
              When NOT to use RN
            </p>
            <p className="mt-3 text-sm leading-relaxed text-jg-fg-muted">
              Heavy realtime 3D, low-latency audio synthesis, or
              platform-specific OS extensions (Live Activities, Widgets,
              Watch faces) deserve native. We ship both — see the{" "}
              <a
                href="/mobile"
                className="underline-offset-2 hover:underline"
                style={{ color: face.themeColor }}
              >
                Mobile cubie
              </a>{" "}
              for native Android, or write us about iOS.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-dashed border-jg-border bg-jg-bg-soft/30 p-5 text-sm text-jg-fg-muted">
          <strong className="block font-display text-jg-fg">
            Featured RN work
          </strong>
          <p className="mt-2">
            {/* TODO João: list 2-3 real RN projects with links + a screenshot for each. */}
            Real shipped RN apps land here — currently behind NDA or in a
            pre-launch state. Reach out via the Hire button for case-study
            references.
          </p>
        </div>
      </div>
    </section>
  );
}
